import { PrePurchaseListGenerator } from '@repo/pdf';
import { ScrollArea } from '@repo/shadcn';
import { FC, useRef } from 'react';

import { IwForm, IwFormRef } from '@components/form';
import { PdfExportIcon } from '@components/icons';
import { HeaderBreadcrumb } from '@components/ui';

import { Route } from '@/constants';

import { BrandSelector } from './brand-selector.component';
import {
    CreatePrePurchaseProvider,
    useCreatePrePurchaseContext,
} from './create-pre-purchase.context';
import {
    CreatePrePurchaseFormSchema,
    INITIAL_VALUES,
    TCreatePrePurchaseForm,
} from './create-pre-purchase.schema';
import { CreatePurchaseItemList } from './item-list.component';
import { PrePurchaseItemsFieldArrayProvider } from './items-field-array.context';
import { OverviewAndSubmit } from './overview-and-submit.component';
import { ProductSelector } from './product-selector.component';
import { SupplierSelector } from './supplier-selector.component';

type CreatePrePurchaseComponentProps = {
    initialValues?: TCreatePrePurchaseForm;
    isEditMode?: boolean;
};
export const CreatePrePurchaseComponent: FC<
    CreatePrePurchaseComponentProps
> = ({ initialValues = INITIAL_VALUES, isEditMode = false }) => {
    const formRef = useRef<IwFormRef<TCreatePrePurchaseForm>>(null);
    const { handleSubmit } = useCreatePrePurchaseContext();

    const handleExportToPdf = () => {
        if (formRef.current) {
            const formData = formRef.current.getValues();

            const pdf = new PrePurchaseListGenerator({
                items:
                    formData?.items?.map((item) => ({
                        productName: item.product_name,
                        quantity: Number(item.quantity),
                        retailPrice: Number(item.retail_price),
                    })) || [],
                supplierName: formData.supplier_name,
                brandName: formData.brand_name,
            });

            pdf.print();
        }
    };

    return (
        <IwForm
            schema={CreatePrePurchaseFormSchema}
            initialValues={initialValues}
            ref={formRef}
            onSubmit={(data) =>
                handleSubmit(data as TCreatePrePurchaseForm, isEditMode)
            }
            logErrors={true}
        >
            <PrePurchaseItemsFieldArrayProvider>
                <div className={'flex flex-row items-start justify-between'}>
                    <HeaderBreadcrumb
                        items={[
                            {
                                label: 'Pre Purchases',
                                href: Route.PRE_PURCHASES,
                            },
                            {
                                label: isEditMode
                                    ? 'Edit Pre Purchase'
                                    : 'Create Pre Purchase',
                            },
                        ]}
                    />
                    <button
                        type={'button'}
                        className={'scale-[1.2]'}
                        onClick={handleExportToPdf}
                    >
                        <PdfExportIcon />
                    </button>{' '}
                </div>

                <div
                    className={
                        'relative grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-2'
                    }
                >
                    <div
                        className={
                            'h-[calc(100vh_-_120px_-_70px)] w-full min-w-0 space-y-2 overflow-y-auto pr-1'
                        }
                    >
                        <CreatePurchaseItemList />
                    </div>

                    <ScrollArea
                        className={'h-[calc(100vh_-_120px)] gap-2 space-y-2'}
                    >
                        <SupplierSelector />
                        <BrandSelector />
                        <ProductSelector />
                    </ScrollArea>

                    <OverviewAndSubmit />
                </div>
            </PrePurchaseItemsFieldArrayProvider>
        </IwForm>
    );
};

export const CreatePrePurchase = () => {
    return (
        <CreatePrePurchaseProvider>
            <CreatePrePurchaseComponent />
        </CreatePrePurchaseProvider>
    );
};

CreatePrePurchase.displayName = 'CreatePrePurchase';
